import { useEffect, useState, useCallback } from "react";
import { revenueWalletApi } from "@/lib/api-client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Wallet, Landmark, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RevenueWallet() {
  const [state, setState] = useState(null);
  const [bankOpen, setBankOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ account_holder_name: "", routing_number: "", account_number: "", bank_name: "" });
  const [amount, setAmount] = useState("");

  const load = useCallback(async () => {
    try { setState(await revenueWalletApi.get()); }
    catch (e) { console.error("wallet load failed", e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const connectBank = async () => {
    setBusy(true);
    try {
      const r = await revenueWalletApi.connectBank(form);
      toast.success(`Business bank connected — ${r.bank.bank_name} …${r.bank.last4}`);
      setBankOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bank connection failed");
    } finally { setBusy(false); }
  };

  const deploy = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      const r = await revenueWalletApi.deploy(amt);
      toast.success(`$${amt.toFixed(2)} deployed — Stripe payout ${r.payout.stripe_payout_id}`);
      setDeployOpen(false);
      setAmount("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deployment failed — funds not moved");
    } finally { setBusy(false); }
  };

  const bank = state?.bank || {};

  return (
    <section className="uc-card p-6 uc-fade" data-testid="revenue-wallet">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="uc-chip mb-2"><Wallet size={11} className="text-emerald-400"/> SOVEREIGN REVENUE WALLET</div>
          <h2 className="font-serif-display text-2xl">Gig income, staged for deployment.</h2>
        </div>
        <div className="flex items-center gap-2">
          {bank.connected ? (
            <span className="uc-chip text-emerald-400 border-emerald-500/40 flex items-center gap-1" data-testid="wallet-bank-chip">
              <Landmark size={10}/> {bank.bank_name || "Bank"} …{bank.last4}
            </span>
          ) : (
            <button
              data-testid="wallet-connect-bank-btn"
              onClick={() => setBankOpen(true)}
              className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60 hover:text-white px-3 py-1.5 border border-white/15 hover:border-white/30"
            ><Landmark size={10} className="inline mr-1.5 -mt-0.5"/>Connect Bank</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="border border-amber-500/25 bg-black p-4">
          <div className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-400/70">Pending</div>
          <div className="font-serif-display text-3xl text-amber-400 mt-1" data-testid="wallet-pending">
            ${Number(state?.pending ?? 0).toFixed(2)}
          </div>
          <p className="text-[9px] text-white/35 mt-1">Awaiting your Mark Paid confirmation</p>
        </div>
        <div className="border border-emerald-500/25 bg-black p-4">
          <div className="font-mono-uc text-[9px] uppercase tracking-widest text-emerald-400/70">Available</div>
          <div className="font-serif-display text-3xl text-emerald-400 mt-1" data-testid="wallet-available">
            ${Number(state?.available ?? 0).toFixed(2)}
          </div>
          <p className="text-[9px] text-white/35 mt-1">Confirmed cash, ready to deploy</p>
        </div>
        <div className="border border-white/10 bg-black p-4 flex flex-col">
          <div className="font-mono-uc text-[9px] uppercase tracking-widest text-white/40">Deployed</div>
          <div className="font-serif-display text-3xl text-white/85 mt-1" data-testid="wallet-deployed">
            ${Number(state?.deployed_total ?? 0).toFixed(2)}
          </div>
          <button
            data-testid="wallet-deploy-btn"
            onClick={() => setDeployOpen(true)}
            disabled={!bank.connected || (state?.available ?? 0) <= 0}
            className="mt-auto flex items-center justify-center gap-1.5 font-mono-uc text-[9px] uppercase tracking-widest text-emerald-400 hover:text-emerald-200 px-2 py-1.5 border border-emerald-500/40 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
          ><Send size={10}/> Deploy Funds</button>
        </div>
      </div>

      {(state?.recent_payouts || []).length > 0 && (
        <div className="mt-3 space-y-1" data-testid="wallet-payouts">
          {state.recent_payouts.slice(0, 4).map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-[10px] font-mono-uc text-white/50 border-b border-white/5 py-1 last:border-none">
              <Send size={9} className="text-emerald-400/70"/>
              <span>${p.amount.toFixed(2)} → business bank</span>
              <span className="uppercase tracking-wider text-white/35">{p.status}</span>
              <span className="ml-auto">{p.created_at?.slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Connect bank dialog */}
      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-emerald-500/30 text-white max-w-md rounded-none" aria-describedby={undefined} data-testid="wallet-bank-dialog">
          <DialogHeader>
            <div className="uc-chip mb-1 w-fit"><Landmark size={11} className="text-emerald-400"/> BUSINESS BANK</div>
            <DialogTitle className="font-serif-display text-2xl">Connect payout account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {[
              { k: "account_holder_name", label: "Account Holder", ph: "Umbrella Holdings LLC" },
              { k: "bank_name", label: "Bank Name", ph: "Chase Business" },
              { k: "routing_number", label: "Routing Number (9 digits)", ph: "110000000" },
              { k: "account_number", label: "Account Number", ph: "000123456789" },
            ].map((f) => (
              <div key={f.k} className="space-y-1">
                <Label className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">{f.label}</Label>
                <Input
                  data-testid={`wallet-bank-${f.k}`}
                  value={form[f.k]}
                  onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                  placeholder={f.ph}
                  className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc"
                />
              </div>
            ))}
            <p className="text-[10px] text-white/40">Validated with Stripe. Only the last 4 digits are ever stored.</p>
            <Button data-testid="wallet-bank-submit" onClick={connectBank} disabled={busy} className="uc-btn-amber rounded-none h-11 w-full">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Landmark size={14} className="mr-2"/>}
              Connect Bank Account
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deploy dialog */}
      <Dialog open={deployOpen} onOpenChange={setDeployOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-emerald-500/30 text-white max-w-sm rounded-none" aria-describedby={undefined} data-testid="wallet-deploy-dialog">
          <DialogHeader>
            <div className="uc-chip mb-1 w-fit"><Send size={11} className="text-emerald-400"/> DEPLOY FUNDS</div>
            <DialogTitle className="font-serif-display text-2xl">Send to business bank</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">
                Amount (Available: ${Number(state?.available ?? 0).toFixed(2)})
              </Label>
              <Input
                data-testid="wallet-deploy-amount"
                type="number" min="0.01" step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc"
              />
            </div>
            <p className="text-[10px] text-white/40">Real Stripe payout to {bank.bank_name || "your bank"} …{bank.last4}. If Stripe refuses, nothing moves.</p>
            <Button data-testid="wallet-deploy-submit" onClick={deploy} disabled={busy} className="uc-btn-amber rounded-none h-11 w-full">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Send size={14} className="mr-2"/>}
              Deploy Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
